import React, { useState, useEffect } from 'react';
import { safeFetchJSON } from '../utils/apiHelpers';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DollarSign, TrendingUp, Users, Award, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import useAffiliateConfig from '../hooks/useAffiliateConfig';
import '../styles/premium-animations.css';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

const AffiliateJoinPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { commissionRate, commissionPerSale, loading: configLoading } = useAffiliateConfig();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [formData, setFormData] = useState({
    full_name: user?.name || '',
    company: '',
    website: '',
    twitter: '',
    instagram: '',
    youtube: '',
    tiktok: '',
    payment_method: 'bank_transfer',
    bank_name: '',
    iban: '',
    bic: '',
    paypal_email: '',
    motivation: ''
  });

  // Check if user is already an affiliate
  useEffect(() => {
    const checkAffiliateStatus = async () => {
      if (!user) {
        setCheckingStatus(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/affiliate/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.is_affiliate) {
            // User is already affiliate, redirect to dashboard
            toast.info('Vous êtes déjà affilié');
            navigate('/affiliate-dashboard');
            return;
          }
        }
      } catch (error) {
        console.error('Error checking status:', error);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkAffiliateStatus();
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Vous devez être connecté');
      navigate('/login');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Vous devez être connecté pour devenir affilié');
        navigate('/login');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/affiliate/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update token with new role if returned
        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
          console.log('✅ Token updated with affiliate role');
        }
        
        toast.success('✅ Demande soumise ! Redirection vers votre dashboard...');
        
        // Force page reload to refresh AuthContext with new role
        // This ensures get_current_user fetches updated role from MongoDB
        setTimeout(() => {
          window.location.href = '/affiliate-dashboard';
        }, 1000);
      } else {
        const error = await response.json();
        const errorMessage = error.detail || 'Erreur lors de l\'inscription';
        console.error('Affiliate application error:', errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Network error:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 text-center">
          <DollarSign className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Connexion Requise</h2>
          <p className="text-gray-300 mb-6">Vous devez être connecté pour rejoindre le programme d'affiliation</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-amber-600 text-white py-3 rounded-lg hover:bg-amber-700"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400 mx-auto mb-4"></div>
          <p className="text-white">Vérification du statut...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Gradient Background - Soft Sunset Glow */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 opacity-95" 
             style={{
               background: 'linear-gradient(135deg, #E6E6FA 0%, #F5DEB3 30%, #FFE4E1 60%, #FFC1CC 85%, #FFD9E5 100%)',
               animation: 'gradient-shift 20s ease infinite',
               backgroundSize: '400% 400%'
             }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(216,191,216,0.25),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(255,218,185,0.2),transparent_50%)]" />
        {/* Soft breathing overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-white/5 to-transparent" 
             style={{
               animation: 'breathing-glow 8s ease-in-out infinite'
             }}
        />
      </div>

      <div className="max-w-4xl mx-auto py-12 px-4 relative z-10">
        {/* Header with Premium Animations */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.6, 0.01, 0.05, 0.95] }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full mb-6 relative overflow-hidden"
            style={{
              background: 'rgba(255, 255, 255, 0.35)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(147, 112, 219, 0.4)',
              boxShadow: '0 8px 32px 0 rgba(147, 112, 219, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.4)'
            }}
            whileHover={{ scale: 1.05, boxShadow: '0 12px 48px 0 rgba(147, 112, 219, 0.3)' }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <DollarSign className="w-6 h-6" style={{ color: '#6A5ACD', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
            </motion.div>
            <span className="font-bold text-lg" style={{ 
              color: '#2D2D3D',
              textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)'
            }}>Programme d'Affiliation Premium</span>
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent"
              animate={{ x: [-200, 400] }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            />
          </motion.div>
          
          <motion.h1 
            className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight relative"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <span style={{
              background: 'linear-gradient(135deg, #5B4FC7 0%, #9370DB 40%, #C77DB5 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0 2px 8px rgba(91, 79, 199, 0.3))',
              position: 'relative',
              display: 'inline-block'
            }}>
              Rejoignez l'Excellence
            </span>
            <div style={{
              position: 'absolute',
              inset: '-10px',
              background: 'radial-gradient(circle at 50% 50%, rgba(147, 112, 219, 0.08), transparent 70%)',
              filter: 'blur(20px)',
              zIndex: -1
            }} />
          </motion.h1>
          
          <motion.p 
            className="text-2xl font-medium backdrop-blur-sm inline-block px-8 py-4 rounded-2xl"
            style={{
              background: 'rgba(255, 255, 255, 0.45)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(147, 112, 219, 0.35)',
              color: '#2D2D3D',
              boxShadow: '0 8px 32px rgba(147, 112, 219, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)'
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {configLoading ? (
              <motion.span 
                className="flex items-center gap-2"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <Sparkles className="w-5 h-5" style={{ color: '#9370DB' }} /> Chargement...
              </motion.span>
            ) : (
              <>
                ✨ Gagnez jusqu'à <span className="font-extrabold text-3xl mx-2" style={{ 
                  color: '#5B4FC7',
                  textShadow: '0 2px 4px rgba(91, 79, 199, 0.2)'
                }}>{commissionRate}%</span> de commission récurrente
              </>
            )}
          </motion.p>
        </motion.div>

        {/* Benefits - Premium Glassmorphism Cards */}
        <motion.div 
          className="grid md:grid-cols-3 gap-8 mb-16"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.15, delayChildren: 0.5 }
            }
          }}
          initial="hidden"
          animate="visible"
        >
          {/* Card 1 - Commission */}
          <motion.div
            variants={{
              hidden: { y: 50, opacity: 0, scale: 0.9 },
              visible: { 
                y: 0, 
                opacity: 1, 
                scale: 1,
                transition: { type: 'spring', stiffness: 80, damping: 12 }
              }
            }}
            className="group relative overflow-hidden rounded-3xl p-8"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.5), rgba(255, 240, 255, 0.4))',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(147, 112, 219, 0.35)',
              boxShadow: '0 8px 32px 0 rgba(147, 112, 219, 0.15), inset 0 1px 0 0 rgba(255, 255, 255, 0.6)'
            }}
            whileHover={{ 
              y: -6, 
              boxShadow: '0 16px 48px 0 rgba(221, 160, 221, 0.25), inset 0 1px 0 0 rgba(255, 255, 255, 0.3)',
              transition: { duration: 0.4 }
            }}
          >
            <motion.div
              className="absolute inset-0"
              style={{
                background: 'radial-gradient(circle at 50% 50%, rgba(221, 160, 221, 0.08), transparent 70%)'
              }}
              animate={{ 
                scale: [1, 1.1, 1],
                opacity: [0.3, 0.5, 0.3]
              }}
              transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              whileHover={{ rotate: 360, scale: 1.1 }}
              transition={{ duration: 0.8 }}
              className="relative z-10"
            >
              <DollarSign className="w-14 h-14 mb-6 filter drop-shadow-md" style={{ color: '#D8BFD8' }} />
            </motion.div>
            {configLoading ? (
              <motion.div 
                className="relative z-10"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#4A4A5E' }}>Chargement...</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#6A6A7E' }}>Chargement...</p>
              </motion.div>
            ) : (
              <div className="relative z-10">
                <h3 className="text-3xl font-extrabold mb-3 flex items-center gap-2">
                  <span className="text-transparent bg-clip-text" style={{ 
                    backgroundImage: 'linear-gradient(135deg, #5B4FC7, #9370DB)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 2px 4px rgba(91, 79, 199, 0.2))'
                  }}>
                    {commissionRate}%
                  </span>
                  <span className="text-xl font-bold" style={{ 
                    color: '#2D2D3D',
                    textShadow: '0 1px 2px rgba(255, 255, 255, 0.5)'
                  }}>de Commission</span>
                </h3>
                <p className="text-sm leading-relaxed font-medium" style={{ color: '#3D3D4D' }}>
                  <span className="font-bold text-base" style={{ 
                    color: '#5B4FC7',
                    textShadow: '0 1px 2px rgba(255, 255, 255, 0.5)'
                  }}>{commissionPerSale.toFixed(2)}€</span> par abonnement premium récurrent chaque mois
                </p>
              </div>
            )}
          </motion.div>

          {/* Card 2 - Récurrentes */}
          <motion.div
            variants={{
              hidden: { y: 50, opacity: 0, scale: 0.9 },
              visible: { 
                y: 0, 
                opacity: 1, 
                scale: 1,
                transition: { type: 'spring', stiffness: 80, damping: 12 }
              }
            }}
            className="group relative overflow-hidden rounded-3xl p-8"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.5), rgba(240, 255, 245, 0.4))',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(102, 194, 102, 0.35)',
              boxShadow: '0 8px 32px 0 rgba(102, 194, 102, 0.15), inset 0 1px 0 0 rgba(255, 255, 255, 0.6)'
            }}
            whileHover={{ 
              y: -6, 
              boxShadow: '0 16px 48px 0 rgba(152, 251, 152, 0.25), inset 0 1px 0 0 rgba(255, 255, 255, 0.3)',
              transition: { duration: 0.4 }
            }}
          >
            <motion.div
              className="absolute inset-0"
              style={{
                background: 'radial-gradient(circle at 50% 50%, rgba(152, 251, 152, 0.08), transparent 70%)'
              }}
              animate={{ 
                scale: [1, 1.1, 1],
                opacity: [0.3, 0.5, 0.3]
              }}
              transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            />
            <motion.div
              whileHover={{ scale: 1.1, y: -5 }}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <TrendingUp className="w-14 h-14 mb-6 filter drop-shadow-md" style={{ color: '#90EE90' }} />
            </motion.div>
            <div className="relative z-10">
              <h3 className="text-2xl font-extrabold mb-3" style={{ color: '#2D2D3D' }}>
                Commissions <span className="text-transparent bg-clip-text" style={{
                  backgroundImage: 'linear-gradient(135deg, #66C266, #7AB8DE)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 2px 4px rgba(102, 194, 102, 0.2))'
                }}>Récurrentes</span>
              </h3>
              <p className="text-sm leading-relaxed font-medium" style={{ 
                color: '#3D3D4D',
                textShadow: '0 1px 2px rgba(255, 255, 255, 0.5)'
              }}>
                Gagnez à chaque renouvellement mensuel de vos filleuls. Revenus passifs garantis.
              </p>
            </div>
          </motion.div>

          {/* Card 3 - Dashboard */}
          <motion.div
            variants={{
              hidden: { y: 50, opacity: 0, scale: 0.9 },
              visible: { 
                y: 0, 
                opacity: 1, 
                scale: 1,
                transition: { type: 'spring', stiffness: 80, damping: 12 }
              }
            }}
            className="group relative overflow-hidden rounded-3xl p-8"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.5), rgba(245, 240, 255, 0.4))',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(123, 104, 214, 0.35)',
              boxShadow: '0 8px 32px 0 rgba(123, 104, 214, 0.15), inset 0 1px 0 0 rgba(255, 255, 255, 0.6)'
            }}
            whileHover={{ 
              y: -6, 
              boxShadow: '0 16px 48px 0 rgba(147, 112, 219, 0.25), inset 0 1px 0 0 rgba(255, 255, 255, 0.3)',
              transition: { duration: 0.4 }
            }}
          >
            <motion.div
              className="absolute inset-0"
              style={{
                background: 'radial-gradient(circle at 50% 50%, rgba(147, 112, 219, 0.08), transparent 70%)'
              }}
              animate={{ 
                scale: [1, 1.1, 1],
                opacity: [0.3, 0.5, 0.3]
              }}
              transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
            />
            <motion.div
              animate={{ rotate: [0, 8, -8, 0] }}
              transition={{ duration: 5, repeat: Infinity }}
              className="relative z-10"
            >
              <Award className="w-14 h-14 mb-6 filter drop-shadow-md" style={{ color: '#9370DB' }} />
            </motion.div>
            <div className="relative z-10">
              <h3 className="text-2xl font-extrabold mb-3" style={{ color: '#2D2D3D' }}>
                Dashboard <span className="text-transparent bg-clip-text" style={{
                  backgroundImage: 'linear-gradient(135deg, #7B68D6, #C29FD8)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 2px 4px rgba(123, 104, 214, 0.2))'
                }}>Premium</span>
              </h3>
              <p className="text-sm leading-relaxed font-medium" style={{ 
                color: '#3D3D4D',
                textShadow: '0 1px 2px rgba(255, 255, 255, 0.5)'
              }}>
                Tracking en temps réel, analytics avancés et paiements automatiques sécurisés.
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Form */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block font-semibold mb-2" style={{ color: '#2D2D3D' }}>Email *</label>
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className="w-full px-4 py-3 rounded-lg border"
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(147, 112, 219, 0.3)',
                    color: '#2D2D3D'
                  }}
                />
              </div>
              <div>
                <label className="block font-semibold mb-2" style={{ color: '#2D2D3D' }}>Nom complet *</label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  placeholder="Ex: Sarah Dubois"
                  className="w-full px-4 py-3 rounded-lg border placeholder-gray-500"
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(147, 112, 219, 0.3)',
                    color: '#2D2D3D'
                  }}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block font-semibold mb-2" style={{ color: '#2D2D3D' }}>Entreprise / Marque</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder="Ex: BeautyZone Paris"
                  className="w-full px-4 py-3 rounded-lg border placeholder-gray-500"
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(147, 112, 219, 0.3)',
                    color: '#2D2D3D'
                  }}
                />
              </div>
              <div>
                <label className="block font-semibold mb-2" style={{ color: '#2D2D3D' }}>Site web / Blog</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  placeholder="https://votre-site.com"
                  className="w-full px-4 py-3 rounded-lg border placeholder-gray-500"
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(147, 112, 219, 0.3)',
                    color: '#2D2D3D'
                  }}
                />
              </div>
            </div>

            <div>
              <h3 className="font-bold mb-4 text-xl" style={{ color: '#2D2D3D' }}>Réseaux sociaux (optionnel)</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={formData.twitter}
                  onChange={(e) => setFormData({...formData, twitter: e.target.value})}
                  placeholder="@votre_twitter"
                  className="w-full px-4 py-3 rounded-lg border placeholder-gray-500"
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(147, 112, 219, 0.3)',
                    color: '#2D2D3D'
                  }}
                />
                <input
                  type="text"
                  value={formData.instagram}
                  onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                  placeholder="@votre_instagram"
                  className="w-full px-4 py-3 rounded-lg border placeholder-gray-500"
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(147, 112, 219, 0.3)',
                    color: '#2D2D3D'
                  }}
                />
                <input
                  type="text"
                  value={formData.youtube}
                  onChange={(e) => setFormData({...formData, youtube: e.target.value})}
                  placeholder="Chaîne YouTube"
                  className="w-full px-4 py-3 rounded-lg border placeholder-gray-500"
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(147, 112, 219, 0.3)',
                    color: '#2D2D3D'
                  }}
                />
                <input
                  type="text"
                  value={formData.tiktok}
                  onChange={(e) => setFormData({...formData, tiktok: e.target.value})}
                  placeholder="@votre_tiktok"
                  className="w-full px-4 py-3 rounded-lg border placeholder-gray-500"
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(147, 112, 219, 0.3)',
                    color: '#2D2D3D'
                  }}
                />
              </div>
            </div>

            <div>
              <h3 className="font-bold mb-4 text-xl" style={{ color: '#2D2D3D' }}>Méthode de paiement préférée *</h3>
              <div className="space-y-4">
                <label className="flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all" style={{
                  background: 'rgba(255, 255, 255, 0.5)',
                  borderColor: formData.payment_method === 'bank_transfer' ? 'rgba(147, 112, 219, 0.6)' : 'rgba(147, 112, 219, 0.3)'
                }}>
                  <input
                    type="radio"
                    name="payment_method"
                    value="bank_transfer"
                    checked={formData.payment_method === 'bank_transfer'}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                    className="w-5 h-5"
                  />
                  <span className="font-medium" style={{ color: '#2D2D3D' }}>🏦 Virement bancaire (SEPA)</span>
                </label>
                <label className="flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all" style={{
                  background: 'rgba(255, 255, 255, 0.5)',
                  borderColor: formData.payment_method === 'paypal' ? 'rgba(147, 112, 219, 0.6)' : 'rgba(147, 112, 219, 0.3)'
                }}>
                  <input
                    type="radio"
                    name="payment_method"
                    value="paypal"
                    checked={formData.payment_method === 'paypal'}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                    className="w-5 h-5"
                  />
                  <span className="font-medium" style={{ color: '#2D2D3D' }}>💳 PayPal</span>
                </label>
              </div>
            </div>

            {formData.payment_method === 'bank_transfer' && (
              <div className="space-y-4">
                <div>
                  <label className="block font-semibold mb-2" style={{ color: '#2D2D3D' }}>Nom de la banque *</label>
                  <input
                    type="text"
                    required
                    value={formData.bank_name}
                    onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                    placeholder="Ex: Crédit Agricole"
                    className="w-full px-4 py-3 rounded-lg border placeholder-gray-500"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-semibold mb-2" style={{ color: '#2D2D3D' }}>IBAN *</label>
                    <input
                      type="text"
                      required
                      value={formData.iban}
                      onChange={(e) => setFormData({...formData, iban: e.target.value})}
                      placeholder="FR76 1234 5678 9012 3456 7890 123"
                      className="w-full px-4 py-3 rounded-lg border placeholder-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-2" style={{ color: '#2D2D3D' }}>BIC/SWIFT *</label>
                    <input
                      type="text"
                      required
                      value={formData.bic}
                      onChange={(e) => setFormData({...formData, bic: e.target.value})}
                      placeholder="AGRIFRPP"
                      className="w-full px-4 py-3 rounded-lg border placeholder-gray-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {formData.payment_method === 'paypal' && (
              <div>
                <label className="block font-semibold mb-2" style={{ color: '#2D2D3D' }}>Email PayPal *</label>
                <input
                  type="email"
                  required
                  value={formData.paypal_email}
                  onChange={(e) => setFormData({...formData, paypal_email: e.target.value})}
                  placeholder="votre-email@example.com"
                  className="w-full px-4 py-3 rounded-lg border placeholder-gray-500"
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(147, 112, 219, 0.3)',
                    color: '#2D2D3D'
                  }}
                />
              </div>
            )}

            <div>
              <label className="block font-semibold mb-2" style={{ color: '#2D2D3D' }}>
                Pourquoi souhaitez-vous devenir affilié Coach DIA ? *
              </label>
              <textarea
                required
                value={formData.motivation}
                onChange={(e) => setFormData({...formData, motivation: e.target.value})}
                placeholder="Expliquez brièvement comment vous comptez promouvoir Coach DIA et pourquoi vous seriez un bon affilié..."
                rows={4}
                className="w-full px-4 py-3 rounded-lg border placeholder-gray-500"
                style={{
                  background: 'rgba(255, 255, 255, 0.7)',
                  borderColor: 'rgba(147, 112, 219, 0.3)',
                  color: '#2D2D3D'
                }}
              />
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/training')}
                className="flex-1 px-6 py-3 rounded-lg font-semibold transition-all"
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  borderColor: 'rgba(147, 112, 219, 0.5)',
                  color: '#5B4FC7',
                  boxShadow: '0 4px 12px rgba(147, 112, 219, 0.15)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                  e.target.style.borderColor = 'rgba(147, 112, 219, 0.7)';
                  e.target.style.boxShadow = '0 6px 20px rgba(147, 112, 219, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.6)';
                  e.target.style.borderColor = 'rgba(147, 112, 219, 0.5)';
                  e.target.style.boxShadow = '0 4px 12px rgba(147, 112, 219, 0.15)';
                }}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold rounded-lg hover:from-amber-600 hover:to-amber-700 disabled:opacity-50"
              >
                {loading ? 'Inscription...' : '🚀 Rejoindre le Programme'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AffiliateJoinPage;
